import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const TONES = {
  paid: "bg-emerald-100 text-emerald-900", succeeded: "bg-emerald-100 text-emerald-900", dispatched: "bg-emerald-100 text-emerald-900", accepted: "bg-emerald-100 text-emerald-900",
  delivered: "bg-emerald-100 text-emerald-900", released: "bg-emerald-100 text-emerald-900", resolved: "bg-emerald-100 text-emerald-900", confirmed: "bg-emerald-100 text-emerald-900", active: "bg-emerald-100 text-emerald-900", published: "bg-emerald-100 text-emerald-900", pass: "bg-emerald-100 text-emerald-900",
  failed: "bg-red-100 text-red-900", dead: "bg-red-100 text-red-900", bounced: "bg-red-100 text-red-900", quarantined: "bg-red-100 text-red-900", recalled: "bg-red-100 text-red-900", open: "bg-amber-100 text-amber-900",
  awaiting_payment: "bg-amber-100 text-amber-900", processing: "bg-amber-100 text-amber-900", pending: "bg-amber-100 text-amber-900", retrying: "bg-amber-100 text-amber-900", queued: "bg-amber-100 text-amber-900",
  partially_dispatched: "bg-sky-100 text-sky-900", packed: "bg-sky-100 text-sky-900", partially_refunded: "bg-sky-100 text-sky-900", refunded: "bg-slate-200 text-slate-800", in_progress: "bg-sky-100 text-sky-900",
};

export const Badge = ({ v, testid }) => <span data-testid={testid} className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded capitalize whitespace-nowrap ${TONES[v] || "bg-slate-100 text-slate-700"}`}>{String(v ?? "—").replace(/_/g, " ")}</span>;
export const TestTag = ({ on }) => (on ? <span className="ml-1 text-[10px] font-bold text-red-700 border border-red-300 rounded px-1">TEST</span> : null);

export const PageHead = ({ title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
    <div><h1 className="text-2xl font-semibold" data-testid="admin-page-title">{title}</h1>{sub && <p className="text-sm text-slate-600 mt-1">{sub}</p>}</div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Btn = ({ children, variant = "primary", className = "", ...p }) => (
  <button {...p} className={`text-sm font-medium rounded-md px-3.5 py-2 transition-colors disabled:opacity-50 ${variant === "primary" ? "bg-[#173B2D] text-white hover:bg-[#0f2a20]" : variant === "danger" ? "bg-red-700 text-white hover:bg-red-800" : "border border-slate-300 bg-white hover:bg-slate-50"} ${className}`}>{children}</button>
);

export const Inp = (p) => <input {...p} className={`border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#A66B24] ${p.className || ""}`} />;
export const Sel = ({ children, ...p }) => <select {...p} className={`border border-slate-300 rounded-md px-3 py-2 text-sm bg-white ${p.className || ""}`}>{children}</select>;
export const L = ({ label, children }) => <label className="grid gap-1 text-sm"><span className="font-medium text-slate-700">{label}</span>{children}</label>;
export const Card = ({ title, children, className = "" }) => <section className={`bg-white border border-slate-200 rounded-lg p-5 ${className}`}>{title && <h2 className="font-semibold mb-4">{title}</h2>}{children}</section>;

export function Table({ cols, rows, empty = "Nothing to show yet.", testid, onSort, sort }) {
  return (
    <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="bg-slate-50 text-left text-slate-600">
          <tr>{cols.map((c) => <th key={c.k} scope="col" className="px-3 py-2.5 font-medium whitespace-nowrap">{c.sort && onSort ? <button onClick={() => onSort(c.k)} className="hover:underline">{c.h}{sort === c.k ? " ↓" : ""}</button> : c.h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={cols.length} className="px-3 py-10 text-center text-slate-500">{empty}</td></tr>
            : rows.map((r, i) => <tr key={r.id || r.user_id || i} className="border-t border-slate-100 hover:bg-slate-50/60">{cols.map((c) => <td key={c.k} className="px-3 py-2.5 align-top">{c.r ? c.r(r) : r[c.k]}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

export const Pager = ({ data, page, setPage }) => data && data.total > data.size ? (
  <div className="flex items-center gap-3 mt-3 text-sm">
    <Btn variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Btn>
    <span>Page {page} of {Math.ceil(data.total / data.size)} · {data.total} total</span>
    <Btn variant="outline" disabled={page * data.size >= data.total} onClick={() => setPage(page + 1)}>Next</Btn>
  </div>
) : data ? <p className="text-xs text-slate-500 mt-2">{data.total} total</p> : null;

export function useFetch(path, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    if (!path) return;
    api.get(path).then((r) => { setData(r.data); setError(null); }).catch((e) => setError(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => { load(); }, [load]);
  return { data, error, reload: load, setData };
}

export async function act(fn, ok = "Saved", after) {
  try {
    const r = await fn();
    if (ok) toast.success(ok);
    after && after(r?.data);
    return r?.data ?? true;
  } catch (e) {
    toast.error(errMsg(e));
    return null;
  }
}

export const Loading = ({ error }) => (error ? <p className="text-red-700 text-sm" role="alert">{error}</p> : <p className="text-sm text-slate-500" aria-busy="true">Loading…</p>);
